import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Link, Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DismissWithReason } from "./dismiss-with-reason";

/**
 * `/agents/tester/capabilities`: what Prokop lacked to verify tickets. Sending one
 * opens a feature for Metoděj; until then it is only a proposal.
 */

type Status = "draft" | "dismissed";

type Learning = {
  id: string;
  title: string;
  detail: string;
  evidenceCount: number;
  status: Status;
  /** Why it was dismissed: Hub's reason, or the note of whoever dismissed it. */
  resolvedNote: string | null;
  reviewedBy: { id: string; name: string | null } | null;
  createdAt: string;
  tickets: Array<{ workItemId: string; globalId: number | null; title: string; at: string }>;
  workItem: { id: string; globalId: number; status: string | null } | null;
};

const TABS = ["draft", "dismissed"] as const;

export function TesterLearningsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Status>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [projects, setProjects] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [target, setTarget] = useState<Record<string, string>>({});

  const load = useCallback(
    (which: Status) => {
      setRows(null);
      return api
        .get<{ learnings: Learning[] }>(`/api/tester-learnings?status=${which}`)
        .then((res) => setRows(res.learnings))
        .catch(() => setError(t("agentTabs.testerLearnings.loadFailed")));
    },
    [t],
  );

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    api
      .get<{ projects: Array<{ id: string; name: string; slug: string }> }>(
        "/api/projects?pageSize=200",
      )
      .then((res) => setProjects(res.projects))
      .catch(() => {
        /* the picker stays empty and sending stays disabled */
      });
  }, [user.globalRole]);

  // Prokop's code is Hub's, so that is where the feature goes unless someone picks otherwise.
  const hubId = projects.find((p) => p.slug === "hub")?.id ?? "";

  async function run(id: string, call: () => Promise<unknown>, failure: string) {
    setBusyId(id);
    setError(null);
    try {
      await call();
      await load(tab);
    } catch {
      setError(failure);
    } finally {
      setBusyId(null);
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div>
        <h2 className="text-xl font-semibold">{t("agentTabs.testerLearnings.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("agentTabs.testerLearnings.intro")}</p>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`tester-learnings-tab-${key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(`agentTabs.testerLearnings.tabs.${key}`)}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="tester-learnings-empty">
          {t("agentTabs.testerLearnings.empty")}
        </p>
      )}

      {rows?.map((r) => {
        const projectId = target[r.id] ?? hubId;
        return (
          <Card key={r.id} data-testid="tester-learning-card">
            <CardContent className="space-y-2 pt-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">
                  {t("agentTabs.testerLearnings.blocked", { count: r.evidenceCount })}
                </Badge>
                {r.workItem && <Badge>{t("agentTabs.testerLearnings.sentBadge")}</Badge>}
              </div>
              <p className="font-medium text-sm">{r.title}</p>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">{r.detail}</p>
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">
                  {t("agentTabs.testerLearnings.tickets")}
                </summary>
                <ul className="mt-1 space-y-0.5">
                  {r.tickets.map((tk) => (
                    <li key={`${tk.workItemId}-${tk.at}`}>
                      {tk.globalId ? (
                        <Link
                          to="/work-item/$id"
                          params={{ id: String(tk.globalId) }}
                          className="underline"
                        >
                          #{tk.globalId}
                        </Link>
                      ) : null}{" "}
                      {tk.title}
                    </li>
                  ))}
                </ul>
              </details>
              {r.status === "dismissed" && r.resolvedNote && (
                <p className="text-xs text-muted-foreground" data-testid="learning-resolved-note">
                  {r.reviewedBy
                    ? t("agentTabs.testerLearnings.why")
                    : t("agentTabs.testerLearnings.dismissedByHub")}
                  {r.resolvedNote}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 border-t pt-2">
                {r.workItem ? (
                  <span
                    className="text-xs text-muted-foreground"
                    data-testid="tester-learning-sent"
                  >
                    {t("agentTabs.testerLearnings.feature")}{" "}
                    <Link
                      to="/work-item/$id"
                      params={{ id: String(r.workItem.globalId) }}
                      className="underline"
                    >
                      #{r.workItem.globalId}
                    </Link>
                    {r.workItem.status ? ` (${r.workItem.status})` : ""}
                  </span>
                ) : (
                  <>
                    <Select
                      size="sm"
                      aria-label={t("agentTabs.testerLearnings.projectFor")}
                      value={projectId}
                      onChange={(v) => setTarget((prev) => ({ ...prev, [r.id]: v }))}
                      options={[
                        { value: "", label: t("agentTabs.testerLearnings.pickProject") },
                        ...projects.map((p) => ({ value: p.id, label: p.name })),
                      ]}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === r.id || !projectId}
                      onClick={() =>
                        void run(
                          r.id,
                          () => api.post(`/api/tester-learnings/${r.id}/send`, { projectId }),
                          t("agentTabs.testerLearnings.sendFailed"),
                        )
                      }
                      data-testid="tester-learning-send"
                    >
                      {t("agentTabs.testerLearnings.send")}
                    </Button>
                  </>
                )}
                {r.status === "draft" ? (
                  <DismissWithReason
                    busy={busyId === r.id}
                    onDismiss={(note) =>
                      void run(
                        r.id,
                        () =>
                          api.patch(`/api/tester-learnings/${r.id}`, { status: "dismissed", note }),
                        t("agentTabs.testerLearnings.saveFailed"),
                      )
                    }
                  />
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() =>
                      void run(
                        r.id,
                        () => api.patch(`/api/tester-learnings/${r.id}`, { status: "draft" }),
                        t("agentTabs.testerLearnings.saveFailed"),
                      )
                    }
                  >
                    {t("agentTabs.testerLearnings.backToProposed")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
